import { useCallback, useEffect, useState } from 'react';
import { api, type Identity } from './api/client';
import type { RuntimeConfig } from './auth/oauth';
import { beginSignIn, completeSignIn, getTokens, onSessionExpired, setTokens } from './auth/session';
import { ErrorBanner } from './components/ErrorBanner';
import { SessionBar } from './components/SessionBar';
import { parseRoute, useRoute, navigate } from './router';
import { AppCreate } from './routes/AppCreate';
import { AppDetail } from './routes/AppDetail';
import { AppsList } from './routes/AppsList';
import { SignedOut } from './routes/SignedOut';

type Status = 'checking' | 'signed-out' | 'signed-in';

export function App({ config }: { config: RuntimeConfig }) {
  const path = useRoute();
  const route = parseRoute(path);

  const [status, setStatus] = useState<Status>('checking');
  const [identity, setIdentity] = useState<Identity | undefined>();
  const [error, setError] = useState<unknown>();

  const signOut = useCallback(() => {
    setTokens(undefined);
    setIdentity(undefined);
    setStatus('signed-out');
  }, []);

  useEffect(() => {
    onSessionExpired(signOut);
  }, [signOut]);

  useEffect(() => {
    // The signed-out screen is deliberately reachable without a session.
    if (route.name === 'signed-out') {
      setStatus('signed-out');
      return;
    }

    if (route.name === 'callback') {
      completeSignIn(config, window.location.search)
        .then((returnTo) => {
          // Replace rather than push, so Back does not land on a spent authorization code.
          window.history.replaceState({}, '', returnTo);
          window.dispatchEvent(new PopStateEvent('popstate'));
          return api.me();
        })
        .then((me) => {
          setIdentity(me);
          setStatus('signed-in');
        })
        .catch((err) => {
          setError(err);
          setStatus('signed-out');
        });
      return;
    }

    if (getTokens()) {
      if (status === 'checking') {
        api
          .me()
          .then((me) => {
            setIdentity(me);
            setStatus('signed-in');
          })
          .catch(() => setStatus('signed-out'));
      }
      return;
    }

    // No tokens: start the redirect, remembering where the user was heading. Cognito's own session
    // cookie usually makes this a no-interaction round trip.
    if (status === 'checking') {
      void beginSignIn(config, path).catch(setError);
    }
  }, [route.name, config, path, status]);

  if (route.name === 'signed-out') {
    return <SignedOut config={config} />;
  }

  if (status !== 'signed-in' || !identity) {
    return (
      <div className="centered">
        {error ? (
          <>
            <h1>Could not sign you in</h1>
            <ErrorBanner error={error} />
            <p>
              <a href="/">Try again</a>
            </p>
          </>
        ) : (
          <p className="muted">Signing in…</p>
        )}
      </div>
    );
  }

  return (
    <>
      <SessionBar identity={identity} config={config} onSignedOut={signOut} />
      <main>
        {/*
          Surfaced from GET /api/me. The only warning today is that the console's own app client has
          lost its group restriction — which closes the console rather than opening it, but is worth
          fixing before everyone is locked out.
        */}
        {identity.warnings.map((warning) => (
          <div key={warning.code} className="callout callout-warn" role="status">
            {warning.message}
          </div>
        ))}

        {route.name === 'list' && <AppsList adminClientId={identity.clientId} />}
        {route.name === 'create' && <AppCreate />}
        {route.name === 'detail' && (
          <AppDetail identifier={route.identifier!} adminClientId={identity.clientId} />
        )}
        {route.name === 'not-found' && (
          <div className="centered">
            <h1>Not found</h1>
            <p>
              <a
                href="/"
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/');
                }}
              >
                Back to the app list
              </a>
            </p>
          </div>
        )}
      </main>
    </>
  );
}
